import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/app/Common";
import { PricingPlans, IntervalToggle } from "@/components/app/PricingPlans";
import { EnterpriseDialog } from "@/components/app/EnterpriseDialog";
import { PaymentHistory } from "@/components/app/PaymentHistory";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";

export default function Billing() {
  const { user, refresh } = useAuth();
  const [interval, setInterval] = useState(user?.billing_interval || "monthly");
  const [busy, setBusy] = useState(null);
  const [ent, setEnt] = useState(false);
  if (user?.workspace?.role !== "owner") {
    return (
      <div>
        <PageHeader eyebrow="Subscription" title="Billing & plans" />
        <div className="surface p-6 text-sm text-muted-foreground" data-testid="billing-member-note">Billing for this workspace is managed by {user?.workspace?.owner_name}.</div>
      </div>
    );
  }
  const select = async (plan, provider = "stripe") => {
    if (plan === "enterprise") return setEnt(true);
    setBusy(plan);
    try {
      const r = await api.post(provider === "paypal" ? "/billing/paypal/checkout" : "/billing/checkout", { plan, interval, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(null);
    }
  };
  const cancel = async () => {
    try {
      await api.post("/billing/cancel");
      toast.success(user?.payment_provider === "paypal" ? "PayPal subscription cancelled. Access continues until the end of the period" : "Subscription will cancel at the end of the period");
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <PageHeader eyebrow="Subscription" title="Billing & plans" subtitle="Upgrade any time. Professional is recommended for most owners." />
      {user?.payment_failed && (
        <div className="mb-6 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-300" data-testid="payment-failed-banner">
          Your last {user.payment_provider === "paypal" ? "PayPal" : "card"} payment failed. Please update your payment method {user.payment_provider === "paypal" ? "in your PayPal account" : "or choose your plan again below"} to keep your modules unlocked.
        </div>
      )}
      <div className="surface p-6 mb-10 flex flex-col md:flex-row md:items-center gap-6" data-testid="current-plan-card">
        <div className="flex-1">
          <div className="eyebrow">Current plan</div>
          <div className="text-2xl font-bold mt-1" data-testid="current-plan-label">{user?.plan_label}</div>
          <div className="text-sm text-muted-foreground mt-1">
            {user?.subscribed_plan ? `Billed ${user.billing_interval} via ${user.payment_provider === "paypal" ? "PayPal" : "card"}${user.cancel_at_period_end ? ` · Cancels ${user.plan_ends_at ? fmtDate(user.plan_ends_at) : "at period end"}` : ""}` : user?.role === "admin" ? "Owner account — all modules unlocked" : `Trial ends ${fmtDate(user?.trial_ends_at)}`}
            {" · "}Entities: {user?.limits?.entities} · Receipts: {user?.limits?.receipts ?? "Unlimited"}
          </div>
        </div>
        {user?.subscribed_plan && !user?.cancel_at_period_end && <Button variant="outline" onClick={cancel} data-testid="cancel-subscription-btn">Cancel subscription</Button>}
      </div>
      <div className="flex justify-end mb-6"><IntervalToggle interval={interval} setInterval={setInterval} /></div>
      <PricingPlans interval={interval} onSelect={select} current={user?.subscribed_plan} busy={busy} paypal />
      <p className="text-xs text-muted-foreground mt-6">Card payments are processed securely by Stripe; PayPal subscriptions are billed and managed by PayPal. Test card: 4242 4242 4242 4242.</p>
      <EnterpriseDialog open={ent} onOpenChange={setEnt} />
      <PaymentHistory />
    </div>
  );
}
